import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const Article = () => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [title, setTitle] = useState("");
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let fileName = params.get("file");

    // Handle specific routes
    if (location.pathname === "/open-source-tools") {
      fileName = "open-source-tools.md";
    }

    if (!fileName) {
      setError("No article specified");
      setLoading(false);
      return;
    }

    const loadArticle = async () => {
      try {
        setLoading(true);

        // Try to load the markdown file from the public directory
        const response = await fetch(`/${fileName}`);

        if (!response.ok) {
          throw new Error(`Failed to load article: ${response.statusText}`);
        }

        const text = await response.text();

        // Extract title from the first H1 in the markdown and clean it up
        const titleMatch = text.match(/^#\s+(.+)$/m);
        let processedContent = text;

        if (titleMatch) {
          // Remove markdown formatting from title (**, *, etc.)
          const cleanTitle = titleMatch[1]
            .replace(/\*\*(.+?)\*\*/g, "$1") // Remove **bold**
            .replace(/\*(.+?)\*/g, "$1") // Remove *italic*
            .replace(/`(.+?)`/g, "$1") // Remove `code`
            .replace(/~~(.+?)~~/g, "$1") // Remove ~~strikethrough~~
            .trim();
          setTitle(cleanTitle);

          // Remove the first H1 from content to avoid duplication
          processedContent = text.replace(/^#\s+.+$/m, "").trim();
        } else {
          setTitle(fileName.replace(".md", "").replace(/-/g, " "));
        }

        setContent(processedContent);

        setError("");
      } catch (err) {
        console.error("Error loading article:", err);
        setError(`Failed to load article: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [location.search, location.pathname]);

  if (loading) {
    return (
      <main className="container mx-auto px-6 py-12">
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading article...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="container mx-auto px-6 py-12">
        <div className="text-center py-16">
          <Link
            to="/"
            className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-white mb-4">
            Article Not Found
          </h1>
          <p className="text-gray-400 text-lg">{error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="container mx-auto px-6 py-12">
      <article className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link
            to="/educational-topics"
            className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Educational Topics
          </Link>
          {title && (
            <h1 className="text-4xl font-bold text-white mb-4">{title}</h1>
          )}
        </div>

        <div className="prose prose-lg prose-invert max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Customize heading styles
              h1: ({ children }) => (
                <h1 className="text-3xl font-bold text-white mt-8 mb-4 border-b border-gray-700 pb-2">
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-2xl font-semibold text-white mt-6 mb-3">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-xl font-semibold text-white mt-5 mb-2">
                  {children}
                </h3>
              ),
              h4: ({ children }) => (
                <h4 className="text-lg font-semibold text-white mt-4 mb-2">
                  {children}
                </h4>
              ),
              // Customize paragraph styles
              p: ({ children }) => (
                <p className="text-gray-300 mb-4 leading-relaxed">{children}</p>
              ),
              // Customize inline text formatting
              strong: ({ children }) => (
                <strong className="font-bold text-white">{children}</strong>
              ),
              em: ({ children }) => (
                <em className="italic text-gray-200">{children}</em>
              ),
              // Customize list styles
              ul: ({ children }) => (
                <ul className="text-gray-300 mb-4 pl-6 space-y-2">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="text-gray-300 mb-4 pl-6 space-y-2">
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li className="list-disc list-outside">{children}</li>
              ),
              // Customize code styles
              code: ({ inline, children }) =>
                inline ? (
                  <code className="bg-gray-800 text-blue-300 px-1 py-0.5 rounded text-sm">
                    {children}
                  </code>
                ) : (
                  <code className="block bg-gray-900 text-green-300 p-4 rounded-lg overflow-x-auto text-sm">
                    {children}
                  </code>
                ),
              pre: ({ children }) => (
                <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto mb-4">
                  {children}
                </pre>
              ),
              // Customize blockquote styles
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-blue-500 pl-4 my-4 italic text-gray-400">
                  {children}
                </blockquote>
              ),
              // Customize link styles
              a: ({ href, children }) => (
                <a
                  href={href}
                  className="text-blue-400 hover:text-blue-300 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {children}
                </a>
              ),
              // Customize table styles
              table: ({ children }) => (
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full border border-gray-700 rounded-lg">
                    {children}
                  </table>
                </div>
              ),
              th: ({ children }) => (
                <th className="bg-gray-800 text-white px-4 py-2 border-b border-gray-700 text-left font-semibold">
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="text-gray-300 px-4 py-2 border-b border-gray-800">
                  {children}
                </td>
              ),
              // Customize horizontal rule
              hr: () => <hr className="border-gray-700 my-8" />,
              // Customize del (strikethrough)
              del: ({ children }) => (
                <del className="line-through text-gray-500">{children}</del>
              ),
              // Ensure proper text rendering
              text: ({ children }) => children,
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </article>
    </main>
  );
};

export default Article;
